/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { DialogLayout } from "@/subframe/layouts/DialogLayout";
import { IconButton } from "@/subframe/components/IconButton";
import { ToggleGroup } from "@/subframe/components/ToggleGroup";
import { TextField } from "@/subframe/components/TextField";
import { Select } from "@/subframe/components/Select";
import * as SubframeCore from "@subframe/core";
import { CategoryLabel } from "@/subframe/components/CategoryLabel";
import { Button } from "@/subframe/components/Button";
import { Calendar } from "@/subframe/components/Calendar";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchLabels } from "../redux/reducers/authSlice";
import { addNotification } from "../redux/reducers/notificationSlice";
import { addEsChatToConfig } from "../Utils/apiUtils";
import axios from "axios";
import { NotificationType, NotificationIcon } from "../Utils/notificationEnums";
import ScheduleMessage from "./templates/ScheduleTemplateMenu";
import type { SchedulePayload } from "./templates/ScheduleTemplateMenu";

const WHATSAPP_TIMEZONES = [
  { name: "Pacific/Niue (GMT-11)", value: "Pacific/Niue" },
  { name: "Pacific/Pago_Pago (GMT-11)", value: "Pacific/Pago_Pago" },
  { name: "Pacific/Honolulu (GMT-10)", value: "Pacific/Honolulu" },
  { name: "Pacific/Tahiti (GMT-10)", value: "Pacific/Tahiti" },
  { name: "Pacific/Marquesas (GMT-9:30)", value: "Pacific/Marquesas" },
  { name: "America/Anchorage (GMT-9)", value: "America/Anchorage" },
  { name: "Pacific/Gambier (GMT-9)", value: "Pacific/Gambier" },
  { name: "America/Los_Angeles (GMT-8)", value: "America/Los_Angeles" },
  { name: "America/Vancouver (GMT-8)", value: "America/Vancouver" },
  { name: "America/Denver (GMT-7)", value: "America/Denver" },
  { name: "America/Phoenix (GMT-7)", value: "America/Phoenix" },
  { name: "America/Chicago (GMT-6)", value: "America/Chicago" },
  { name: "America/Mexico_City (GMT-6)", value: "America/Mexico_City" },
  { name: "America/New_York (GMT-5)", value: "America/New_York" },
  { name: "America/Toronto (GMT-5)", value: "America/Toronto" },
  { name: "America/Halifax (GMT-4)", value: "America/Halifax" },
  { name: "America/Caracas (GMT-4)", value: "America/Caracas" },
  { name: "America/St_Johns (GMT-3:30)", value: "America/St_Johns" },
  { name: "America/Sao_Paulo (GMT-3)", value: "America/Sao_Paulo" },
  { name: "America/Argentina/Buenos_Aires (GMT-3)", value: "America/Argentina/Buenos_Aires" },
  { name: "Atlantic/South_Georgia (GMT-2)", value: "Atlantic/South_Georgia" },
  { name: "Atlantic/Azores (GMT-1)", value: "Atlantic/Azores" },
  { name: "Europe/London (GMT+0)", value: "Europe/London" },
  { name: "Africa/Casablanca (GMT+0)", value: "Africa/Casablanca" },
  { name: "Europe/Paris (GMT+1)", value: "Europe/Paris" },
  { name: "Europe/Berlin (GMT+1)", value: "Europe/Berlin" },
  { name: "Europe/Athens (GMT+2)", value: "Europe/Athens" },
  { name: "Africa/Cairo (GMT+2)", value: "Africa/Cairo" },
  { name: "Europe/Moscow (GMT+3)", value: "Europe/Moscow" },
  { name: "Africa/Nairobi (GMT+3)", value: "Africa/Nairobi" },
  { name: "Asia/Tehran (GMT+3:30)", value: "Asia/Tehran" },
  { name: "Asia/Dubai (GMT+4)", value: "Asia/Dubai" },
  { name: "Europe/Samara (GMT+4)", value: "Europe/Samara" },
  { name: "Asia/Kabul (GMT+4:30)", value: "Asia/Kabul" },
  { name: "Asia/Karachi (GMT+5)", value: "Asia/Karachi" },
  { name: "Asia/Tashkent (GMT+5)", value: "Asia/Tashkent" },
  { name: "Asia/Kolkata (GMT+5:30)", value: "Asia/Kolkata" },
  { name: "Asia/Kathmandu (GMT+5:45)", value: "Asia/Kathmandu" },
  { name: "Asia/Dhaka (GMT+6)", value: "Asia/Dhaka" },
  { name: "Asia/Almaty (GMT+6)", value: "Asia/Almaty" },
  { name: "Asia/Yangon (GMT+6:30)", value: "Asia/Yangon" },
  { name: "Asia/Bangkok (GMT+7)", value: "Asia/Bangkok" },
  { name: "Asia/Jakarta (GMT+7)", value: "Asia/Jakarta" },
  { name: "Asia/Shanghai (GMT+8)", value: "Asia/Shanghai" },
  { name: "Asia/Singapore (GMT+8)", value: "Asia/Singapore" },
  { name: "Asia/Tokyo (GMT+9)", value: "Asia/Tokyo" },
  { name: "Asia/Seoul (GMT+9)", value: "Asia/Seoul" },
  { name: "Australia/Adelaide (GMT+9:30)", value: "Australia/Adelaide" },
  { name: "Australia/Darwin (GMT+9:30)", value: "Australia/Darwin" },
  { name: "Australia/Sydney (GMT+10)", value: "Australia/Sydney" },
  { name: "Pacific/Port_Moresby (GMT+10)", value: "Pacific/Port_Moresby" },
  { name: "Australia/Lord_Howe (GMT+10:30)", value: "Australia/Lord_Howe" },
  { name: "Pacific/Noumea (GMT+11)", value: "Pacific/Noumea" },
  { name: "Asia/Vladivostok (GMT+11)", value: "Asia/Vladivostok" },
  { name: "Pacific/Auckland (GMT+12)", value: "Pacific/Auckland" },
  { name: "Pacific/Fiji (GMT+12)", value: "Pacific/Fiji" },
  { name: "Pacific/Chatham (GMT+12:45)", value: "Pacific/Chatham" },
  { name: "Pacific/Tongatapu (GMT+13)", value: "Pacific/Tongatapu" },
  { name: "Pacific/Apia (GMT+13)", value: "Pacific/Apia" },
  { name: "Pacific/Kiritimati (GMT+14)", value: "Pacific/Kiritimati" },
];

interface NewChatProps {
  isNewChatOpen: boolean;
  setIsNewChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Label {
  id: number;
  label: string;
  user_id: string;
  keywords: string[];
  created_at: number;
}

interface Template {
  id: number;
  user_id: string;
  name: string;
  name_with_details: string;
  category: string;
  type: string;
  allow_category_change: boolean;
  language: string;
  components: {
    name: string;
    category: string;
    language: string;
    components: Array<{
      text?: string;
      type: string;
      format?: string;
      example?: {
        body_text: string[][];
      };
      buttons?: Array<{
        text: string;
        type: string;
        phone_number?: string;
        url?: string;
      }>;
    }>;
  };
  status: string;
  variable_counts: number;
  template_formate: string;
  template_id: string;
}

function ChatSendNewMessage({ isNewChatOpen, setIsNewChatOpen }: NewChatProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { system_user_id } = useSelector((state: RootState) => state.auth);
  const { labels } = useSelector((state: RootState) => state.auth);

  const [recipientType, setRecipientType] = useState<string>("single");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [templateVariables, setTemplateVariables] = useState<{ [key: string]: string }>({});
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState<boolean>(false);
  const [isSingleSchedulePopupOpen, setIsSingleSchedulePopupOpen] = useState<boolean>(false);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState<boolean>(false);
  const [scheduleDate, setScheduleDate] = useState<Date>(new Date());
  const [scheduleHour, setScheduleHour] = useState<string>("12");
  const [scheduleMinute, setScheduleMinute] = useState<string>("00");
  const [scheduleAmPm, setScheduleAmPm] = useState<"AM" | "PM">("AM");
  const [scheduleTimezone, setScheduleTimezone] = useState<string>(WHATSAPP_TIMEZONES[0].value);
  const scheduleDateTimeRef = useRef<string>("");

  // Form state for single recipient
  const [singleRecipientData, setSingleRecipientData] = useState({
    phoneNumber: "",
    assignedLabels: [] as Label[]
  });

  // Form state for multiple recipients
  const [multipleRecipientsData, setMultipleRecipientsData] = useState({
    selectedLabels: [] as Label[],
    newLabels: [] as Label[]
  });

  // Generate UUID for broadcast identifier
  const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Parse template format to get variables
  const parseTemplateFormat = (format: string) => {
    if (!format) return [];
    const variables: { type: string, count: number }[] = [];
    const formatParts = format.slice(1, -1).split(',').map(part => part.trim());

    formatParts.forEach(part => {
      const [type, count] = part.split(':');
      variables.push({ type: type.trim(), count: parseInt(count) });
    });

    return variables;
  };

  // Format date to DD/MM/YYYY HH:mm structure
  const formatDate = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Fetch labels and templates when component mounts
  useEffect(() => {
    if (system_user_id && isNewChatOpen) {
      dispatch(fetchLabels());
      fetchTemplates();
    }
  }, [system_user_id, isNewChatOpen, dispatch]);

  // Auto-set current time when schedule dialog opens
  useEffect(() => {
    if (!isScheduleOpen) return;
    const now = new Date();
    const hour24 = now.getHours();
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const amPm: "AM" | "PM" = hour24 >= 12 ? "PM" : "AM";
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneExists = WHATSAPP_TIMEZONES.some((tz) => tz.value === browserTimezone);
    setScheduleDate(now);
    setScheduleHour(String(hour12).padStart(2, "0"));
    setScheduleMinute(String(now.getMinutes()).padStart(2, "0"));
    setScheduleAmPm(amPm);
    setScheduleTimezone(timezoneExists ? browserTimezone : WHATSAPP_TIMEZONES[0].value);
  }, [isScheduleOpen]);

  // Build schedule datetime string from selected values (no timezone conversion)
  useEffect(() => {
    const parsedHour = parseInt(scheduleHour, 10);
    const parsedMinute = parseInt(scheduleMinute, 10);
    let hour24 = parsedHour % 12;
    if (scheduleAmPm === "PM") hour24 += 12;
    const year = scheduleDate.getFullYear();
    const month = String(scheduleDate.getMonth() + 1).padStart(2, "0");
    const day = String(scheduleDate.getDate()).padStart(2, "0");
    const hour = String(hour24).padStart(2, "0");
    const minute = String(parsedMinute).padStart(2, "0");
    scheduleDateTimeRef.current = `${year}-${month}-${day}T${hour}:${minute}:00Z`;
  }, [scheduleDate, scheduleHour, scheduleMinute, scheduleAmPm]);

  // Fetch templates from API
  const fetchTemplates = async () => {
    try {
      const config = addEsChatToConfig();
      const response = await axios.get('https://api.insightssystem.com/api:bVXsw_FD/web/templates', config);
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id.toString() === templateId);
    setSelectedTemplate(template || null);

    if (template) {
      const newVariables: { [key: string]: string } = {};

      // Initialize body variables from the component
      const bodyComponent = template.components.components.find(
        comp => comp.type === 'BODY'
      );
      if (bodyComponent?.example?.body_text?.[0]) {
        bodyComponent.example.body_text[0].forEach((_, index) => {
          newVariables[`b_${index + 1}`] = '';
        });
      }

      // Initialize media variables from header component
      const headerComponent: any = template.components.components.find(
        comp => comp.type === 'HEADER'
      );
      if (headerComponent?.format) {
        const format = headerComponent.format.toLowerCase();
        if (['image', 'video', 'document'].includes(format)) {
          newVariables['m_1'] = '';
        }
      }

      // Initialize website variables from template_formate
      if (template.template_formate) {
        const formatVariables = parseTemplateFormat(template.template_formate);
        formatVariables.forEach(({ type, count }) => {
          switch (type) {
            case 'vw':
              // Only handle up to 2 website variables
              for (let i = 1; i <= Math.min(count, 2); i++) {
                newVariables[`vw_${i}`] = '';
              }
              break;
            case 'm':
              // If media is specified in format and header exists
              if (headerComponent?.format) {
                const format = headerComponent.format.toLowerCase();
                if (['image', 'video', 'document'].includes(format)) {
                  newVariables['m_1'] = '';
                }
              }
              break;
          }
        });
      }

      setTemplateVariables(newVariables);
    } else {
      setTemplateVariables({});
    }
  };

  // Handle template variable changes
  const handleTemplateVariableChange = (key: string, value: string) => {
    setTemplateVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Validate template variables
  const validateTemplateVariables = (): string[] => {
    if (!selectedTemplate) return [];

    const missingFields: string[] = [];

    // Check header component variable if it exists and is required
    const headerComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'HEADER');
    if (headerComponent?.format && !templateVariables.m_1) {
      missingFields.push(`${headerComponent.format} URL`);
    }

    // Check body variables
    const bodyComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BODY');
    bodyComponent?.example?.body_text?.[0]?.forEach((_: any, index: number) => {
      const key = `b_${index + 1}`;
      if (!templateVariables[key]) {
        missingFields.push(`Body Variable {{${index + 1}}}`);
      }
    });

    // Check website variables if they exist in the template format
    const formatVariables = parseTemplateFormat(selectedTemplate.template_formate);
    const hasWebsiteVariables = formatVariables.some(v => v.type === 'vw');
    if (hasWebsiteVariables) {
      formatVariables
        .filter(v => v.type === 'vw')
        .forEach(({ count }) => {
          for (let i = 1; i <= Math.min(count, 2); i++) {
            const key = `vw_${i}`;
            if (!templateVariables[key]) {
              missingFields.push(`Visit Website URL ${i > 1 ? i : ''}`);
            }
          }
        });
    }

    return missingFields;
  };

  // Send message to single recipient
  const handleSendSingleRecipient = async (schedulePayload?: SchedulePayload) => {
    if (!selectedTemplate || !singleRecipientData.phoneNumber || !system_user_id) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Missing Information",
          message: "Please select a template and enter a phone number",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Validate template variables
    const missingRequiredVariables = validateTemplateVariables();
    if (missingRequiredVariables.length > 0) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Template Validation Failed",
          message: `Please fill in the following required fields: ${missingRequiredVariables.join(', ')}`,
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    const isScheduled = !!schedulePayload?.is_schedule;

    try {
      setIsSending(true);

      // Prepare variables for API
      const variables: any = {};
      Object.entries(templateVariables).forEach(([key, value]) => {
        if (key.startsWith('b_')) {
          variables[`body${key.slice(2)}`] = value;
        } else if (key.startsWith('vw_')) {
          const index = parseInt(key.slice(3));
          if (index <= 2) {
            variables[`visit_website${index > 1 ? index : ''}`] = value;
          }
        } else if (key === 'm_1') {
          variables['media'] = value;
        }
      });

      // Prepare payload
      const payload = {
        template: selectedTemplate.template_id,
        variables,
        current_recipient: singleRecipientData.phoneNumber,
        labels: singleRecipientData.assignedLabels.map(label => label.label),
        ...(schedulePayload ? schedulePayload : { is_schedule: false }),
      };

      // Send to API
      const config = addEsChatToConfig();

      const response = await axios.post('https://api.insightssystem.com/api:bVXsw_FD/web/send/new_chat', payload, config);
      const result = response.data;

      // Check if the API call was successful
      if (!result.success) {
        // Extract error message from API response
        let errorMessage = "Message could not be sent. Please try again.";

        // First try to get error from the direct response structure
        if (result.message) {
          errorMessage = result.message;
        } else {
          // Try to extract error message from WhatsApp response
          const whatsappResponse = result.whatsapp_response_info as any;
          
          if (whatsappResponse) {
            if (whatsappResponse.error_user_msg) {
              errorMessage = whatsappResponse.error_user_msg;
            } else if (whatsappResponse.error_data?.details) {
              errorMessage = whatsappResponse.error_data.details;
            } else if (whatsappResponse.message) {
              errorMessage = whatsappResponse.message;
            }
          }
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Message Failed",
            message: errorMessage,
            icon: NotificationIcon.ERROR,
          })
        );
        return;
      }

      // Success notification
      if (isScheduled) {
        let scheduleMessage = "Your message has been scheduled.";

        if (schedulePayload?.schedule_type === "specific") {
          const formattedLocalDateTime = schedulePayload.schedule_datetime_date
            ? moment(schedulePayload.schedule_datetime_date).local().format("MMM D, YYYY hh:mm A")
            : "selected date and time";
          const selectedTimezone = schedulePayload.time_zone || "your timezone";
          scheduleMessage = `Your message will be sent on ${formattedLocalDateTime} (${selectedTimezone}).`;
        } else if (schedulePayload?.schedule_type === "relative") {
          const unitValue = schedulePayload.unit_of_time_value || "";
          const unitName = schedulePayload.unit_of_time_name || "";
          scheduleMessage = `Your message will be sent after ${unitValue} ${unitName} from now.`;
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Message Scheduled",
            message: scheduleMessage,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Message Sent",
            message: "Your template message has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      // Clear form data
      setSingleRecipientData({
        phoneNumber: "",
        assignedLabels: []
      });
      setSelectedTemplate(null);
      setTemplateVariables({});

      // Close modal
      setIsNewChatOpen(false);

    } catch (error: any) {
      let errorMessage = "Something went wrong. Please try again.";
      
      // Handle axios errors with response data
      if (error.response) {
        const responseData = error.response.data;
        
        // Try to extract error message from the response
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        } else {
          // Fallback to status text or generic message
          errorMessage = error.response.statusText || `Request failed with status code ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Message Not Sent",
          message: errorMessage,
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  // Send broadcast to multiple recipients
  const handleSendMultipleRecipients = async (isScheduled: boolean = false) => {
    if (!selectedTemplate || !system_user_id) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Missing Information",
          message: "Please select a template",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Validate template variables
    const missingRequiredVariables = validateTemplateVariables();
    if (missingRequiredVariables.length > 0) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Template Validation Failed",
          message: `Please fill in the following required fields: ${missingRequiredVariables.join(', ')}`,
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    // Validate that at least one label is selected for recipients
    if (multipleRecipientsData.selectedLabels.length === 0) {
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "No Recipients Selected",
          message: "Please select at least one label to send the message to",
          icon: NotificationIcon.ERROR,
        })
      );
      return;
    }

    try {
      setIsSending(true);

      // Prepare variables for API
      const variables: any = {};
      Object.entries(templateVariables).forEach(([key, value]) => {
        if (key.startsWith('b_')) {
          variables[`body${key.slice(2)}`] = value;
        } else if (key.startsWith('vw_')) {
          const index = parseInt(key.slice(3));
          if (index <= 2) {
            variables[`visit_website${index > 1 ? index : ''}`] = value;
          }
        } else if (key === 'm_1') {
          variables['media'] = value;
        }
      });

      // Prepare payload
      const payload = {
        broadcast_identifier: generateUUID(),
        broadcast_name: "label-based-chat",
        schedule: isScheduled ? scheduleDateTimeRef.current : formatDate(new Date(Date.now() + 60000)),
        selected_labels: multipleRecipientsData.selectedLabels.map(label => label.label),
        new_labels: multipleRecipientsData.newLabels.map(label => label.label),
        template: selectedTemplate.template_id,
        variables,
        is_schedule: isScheduled,
        ...(isScheduled ? {
          schedule_datetime_date: scheduleDateTimeRef.current,
          time_zone: scheduleTimezone,
        } : {})
      };

      // Send to API
      const config = addEsChatToConfig();

      const response = await axios.post('https://api.insightssystem.com/api:bVXsw_FD/web/send/broadcast_schedule', payload, config);
      const result = response.data;

      // Check if the API call was successful
      if (!result.success) {
        // Extract error message from API response
        let errorMessage = "Broadcast could not be sent. Please try again.";

        // First try to get error from the direct response structure
        if (result.message) {
          errorMessage = result.message;
        } else {
          // Try to extract error message from WhatsApp response
          const whatsappResponse = result.whatsapp_response_info as any;
          
          if (whatsappResponse) {
            if (whatsappResponse.error_user_msg) {
              errorMessage = whatsappResponse.error_user_msg;
            } else if (whatsappResponse.error_data?.details) {
              errorMessage = whatsappResponse.error_data.details;
            } else if (whatsappResponse.message) {
              errorMessage = whatsappResponse.message;
            }
          }
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.ERROR,
            title: "Broadcast Failed",
            message: errorMessage,
            icon: NotificationIcon.ERROR,
          })
        );
        return;
      }

      // Success notification
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.SUCCESS,
          title: isScheduled ? "Broadcast Scheduled" : "Broadcast Sent",
          message: isScheduled
            ? `Your broadcast will be sent on ${scheduleDate.toLocaleDateString()} at ${scheduleHour}:${scheduleMinute} ${scheduleAmPm} (${scheduleTimezone})`
            : "Your broadcast has been sent successfully",
          icon: NotificationIcon.SUCCESS,
        })
      );

      // Clear form data
      setMultipleRecipientsData({
        selectedLabels: [],
        newLabels: []
      });
      setSelectedTemplate(null);
      setTemplateVariables({});

      // Close modal
      setIsNewChatOpen(false);

    } catch (error: any) {
      let errorMessage = "Something went wrong. Please try again.";
      
      // Handle axios errors with response data
      if (error.response) {
        const responseData = error.response.data;
        
        // Try to extract error message from the response
        if (responseData && responseData.message) {
          errorMessage = responseData.message;
        } else {
          // Fallback to status text or generic message
          errorMessage = error.response.statusText || `Request failed with status code ${error.response.status}`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: "Broadcast Not Sent",
          message: errorMessage,
          icon: NotificationIcon.ERROR,
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  // Handle schedule button click
  const handleScheduleClick = () => {
    if (recipientType === "single") {
      setIsSingleSchedulePopupOpen(true);
    } else {
      setIsScheduleOpen(true);
    }
  };

  const handleNewChatScheduleFromMenu = (schedulePayload: SchedulePayload) => {
    handleSendSingleRecipient(schedulePayload);
  };

  // Handle schedule confirmation (broadcast only)
  const handleScheduleConfirm = () => {
    handleSendMultipleRecipients(true);
    setIsScheduleOpen(false);
  };

  // Handle single recipient form changes
  const handleSingleRecipientChange = (field: string, value: string) => {
    setSingleRecipientData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  // Handle label selection for single recipient
  const handleSingleRecipientLabelSelect = (labelId: string) => {
    const selectedLabel = labels.find(label => label.id.toString() === labelId);
    if (selectedLabel && !singleRecipientData.assignedLabels.find(label => label.id === selectedLabel.id)) {
      setSingleRecipientData(prev => ({
        ...prev,
        assignedLabels: [...prev.assignedLabels, selectedLabel]
      }));
    }
  };

  // Handle label removal for single recipient
  const handleSingleRecipientLabelRemove = (labelId: number) => {
    setSingleRecipientData(prev => ({
      ...prev,
      assignedLabels: prev.assignedLabels.filter(label => label.id !== labelId)
    }));
  };

  // Handle label selection for multiple recipients (for selecting recipients by labels)
  const handleMultipleRecipientsLabelSelect = (labelId: string) => {
    const selectedLabel = labels.find(label => label.id.toString() === labelId);
    if (selectedLabel && !multipleRecipientsData.selectedLabels.find(label => label.id === selectedLabel.id)) {
      setMultipleRecipientsData(prev => ({
        ...prev,
        selectedLabels: [...prev.selectedLabels, selectedLabel]
      }));
    }
  };

  // Handle label removal for multiple recipients (for selecting recipients by labels)
  const handleMultipleRecipientsLabelRemove = (labelId: number) => {
    setMultipleRecipientsData(prev => ({
      ...prev,
      selectedLabels: prev.selectedLabels.filter(label => label.id !== labelId)
    }));
  };

  // Handle new label assignment for multiple recipients
  const handleMultipleRecipientsNewLabelSelect = (labelId: string) => {
    const selectedLabel = labels.find(label => label.id.toString() === labelId);
    if (selectedLabel && !multipleRecipientsData.newLabels.find(label => label.id === selectedLabel.id)) {
      setMultipleRecipientsData(prev => ({
        ...prev,
        newLabels: [...prev.newLabels, selectedLabel]
      }));
    }
  };

  // Handle new label removal for multiple recipients
  const handleMultipleRecipientsNewLabelRemove = (labelId: number) => {
    setMultipleRecipientsData(prev => ({
      ...prev,
      newLabels: prev.newLabels.filter(label => label.id !== labelId)
    }));
  };

  // Get available labels for dropdown (filter out already selected ones)
  const getAvailableLabelsForSingle = () => {
    const selectedIds = singleRecipientData.assignedLabels.map(label => label.id);
    return labels.filter(label => !selectedIds.includes(label.id));
  };

  const getAvailableLabelsForMultiple = () => {
    const selectedIds = multipleRecipientsData.selectedLabels.map(label => label.id);
    return labels.filter(label => !selectedIds.includes(label.id));
  };

  const getAvailableLabelsForNewAssignment = () => {
    const selectedIds = multipleRecipientsData.newLabels.map(label => label.id);
    return labels.filter(label => !selectedIds.includes(label.id));
  };

  // Generate template variable fields
  const renderTemplateVariables = () => {
    if (!selectedTemplate) return null;

    const headerComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'HEADER');
    const bodyComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BODY');
    const formatVariables = parseTemplateFormat(selectedTemplate.template_formate);

    return (
      <div className="conveythis-no-translate flex w-full flex-col items-start gap-4">
        {/* Media Variable (Header) */}
        {headerComponent?.format && ['image', 'video', 'document'].includes(headerComponent.format.toLowerCase()) && (
          <TextField
            className="h-auto w-full flex-none"
            label={`${headerComponent.format} Variable (See supported URLs)`}
            helpText={`Insert a supported ${headerComponent.format.toLowerCase()} URL for new message.`}
            icon="FeatherLink"
            iconRight="FeatherLink"
          >
            <TextField.Input
              placeholder={`Enter ${headerComponent.format.toLowerCase()} URL`}
              value={templateVariables.m_1 || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleTemplateVariableChange("m_1", event.target.value)
              }
            />
          </TextField>
        )}

        {/* Body Variables */}
        {bodyComponent?.example?.body_text?.[0]?.map((_: any, index: number) => (
          <TextField
            key={`body_${index + 1}`}
            className="h-auto w-full flex-none"
            label={`Body Variable {{${index + 1}}}`}
            helpText=""
            icon="FeatherCurlyBraces"
            iconRight="FeatherText"
          >
            <TextField.Input
              placeholder={`e.g., ${bodyComponent.example.body_text[0][index] || `Variable ${index + 1}`}`}
              value={templateVariables[`b_${index + 1}`] || ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleTemplateVariableChange(`b_${index + 1}`, event.target.value)
              }
            />
          </TextField>
        ))}

        {/* Website Variables */}
        {formatVariables
          .filter(v => v.type === 'vw')
          .map(({ count }) => {
            const maxCount = Math.min(count, 2);
            return Array.from({ length: maxCount }, (_, i) => i + 1).map(index => (
              <TextField
                key={`website_${index}`}
                className="h-auto w-full flex-none"
                label={`Button Variable {{${index}}}`}
                helpText="Insert the dynamic URL for your 'Visit Website' button here."
                icon="FeatherCurlyBraces"
                iconRight="FeatherText"
              >
                <TextField.Input
                  placeholder={`e.g., https://example.com`}
                  value={templateVariables[`vw_${index}`] || ""}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleTemplateVariableChange(`vw_${index}`, event.target.value)
                  }
                />
              </TextField>
            ));
          })}
      </div>
    );
  };

  // Render header media preview
  const renderHeaderMedia = (component: any) => {
    if (!component?.format) return null;

    switch (component.format.toLowerCase()) {
      case 'image':
        return (
          <div className="h-48 w-full flex-none rounded-t-lg bg-neutral-100 flex flex-col items-center justify-center">
            <SubframeCore.Icon
              className="text-neutral-400"
              name="FeatherImage"
            />
            <span className="mt-2 text-sm text-neutral-500">Image Preview</span>
          </div>
        );
      case 'video':
        return (
          <div className="h-48 w-full flex-none rounded-t-lg bg-neutral-100 flex flex-col items-center justify-center">
            <SubframeCore.Icon
              className="text-neutral-400"
              name="FeatherVideo"
            />
            <span className="mt-2 text-sm text-neutral-500">Video Preview</span>
          </div>
        );
      case 'document':
        return (
          <div className="h-48 w-full flex-none rounded-t-lg bg-neutral-100 flex flex-col items-center justify-center">
            <SubframeCore.Icon
              className="text-neutral-400"
              name="FeatherFileText"
            />
            <span className="mt-2 text-sm text-neutral-500">Document Preview</span>
          </div>
        );
      default:
        return null;
    }
  };

  // Generate template preview
  const renderTemplatePreview = () => {
    if (!selectedTemplate) {
      return (
        <div className="conveythis-no-translate flex max-w-[240px] flex-col items-start rounded-lg border border-solid border-neutral-border bg-[#ffffffff] shadow-md">
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 px-4 pt-4 pb-0.5">
            <span className="font-['Nunito'] text-[13px] font-[600] leading-[20px] text-default-font">
              Select a template to see the preview
            </span>
          </div>
        </div>
      );
    }

    const headerComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'HEADER');
    const bodyComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BODY');
    const buttonComponent: any = selectedTemplate.components.components.find(comp => comp.type === 'BUTTONS');

    let previewText = bodyComponent?.text || "Template preview will appear here";

    // Replace variables in preview text
    Object.entries(templateVariables).forEach(([key, value]) => {
      if (key.startsWith('b_')) {
        const index = parseInt(key.slice(2));
        const placeholder = `{{${index}}}`;
        previewText = previewText.replace(placeholder, value || placeholder);
      }
    });

    // Format text with proper direction and line breaks
    const formatText = (text: string) => {
      if (!text) return '';
      const rtlLanguages = ['ar', 'he', 'ur', 'fa'];
      const isRTL = rtlLanguages.includes(selectedTemplate?.language?.toLowerCase());

      return (
        <span
          className={`whitespace-pre-wrap w-full text-xs leading-relaxed ${isRTL ? 'text-right' : 'text-left'
            }`}
          dir={isRTL ? 'rtl' : 'ltr'}
          dangerouslySetInnerHTML={{
            __html: text.split('\n').join('<br />')
          }}
        />
      );
    };

    return (
      <div className="conveythis-no-translate flex max-w-[240px] flex-col items-start rounded-lg border border-solid border-neutral-border bg-[#ffffffff] shadow-md">
        {renderHeaderMedia(headerComponent)}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 px-4 pt-4 pb-0.5">
          {formatText(previewText)}
        </div>
        <div className="flex w-full flex-col items-start">
          {buttonComponent?.buttons?.map((button: any, index: any) => (
            <div key={index} className="flex w-full flex-col items-center justify-center gap-2 border-t-2 border-solid border-neutral-border px-3 py-3">
              <div className="flex items-center gap-2">
                <SubframeCore.Icon
                  className="text-heading-3 font-heading-3 text-[#10b989ff]"
                  name={
                    button.type === 'QUICK_REPLY' ? 'FeatherReply' :
                      button.type === 'URL' ? 'FeatherArrowUpRightSquare' :
                        button.type === 'PHONE_NUMBER' ? 'FeatherPhone' :
                          button.type === 'COPY_CODE' ? 'FeatherCopy' : 'FeatherList'
                  }
                />
                <span className="font-['Nunito'] text-xs font-[600] leading-[20px] text-success-500">
                  {button.text}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <DialogLayout open={isNewChatOpen} onOpenChange={setIsNewChatOpen}>
        <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-2 py-2">
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 bg-default-background px-4 py-4">
            <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Send Message
                </span>
                <span className="text-body font-body text-subtext-color">
                  Choose recipient type and compose your message
                </span>
              </div>
              <IconButton
                icon="FeatherX"
                onClick={() => setIsNewChatOpen(false)}
              />
            </div>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-center justify-center">
                <ToggleGroup
                  value={recipientType}
                  onValueChange={(value: string) => {
                    // Only update if a valid value is provided and it's different
                    if (value && (value === "single" || value === "multiple")) {
                      setRecipientType(value);
                    }
                  }}
                >
                  <ToggleGroup.Item icon="FeatherUser" value="single">
                    Single Recipient
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon="FeatherUsers" value="multiple">
                    Broadcast
                  </ToggleGroup.Item>
                </ToggleGroup>
              </div>

              {/* Single Recipient Form */}
              {recipientType === "single" && (
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
                    <TextField
                      className="h-auto w-full flex-none"
                      label="Phone Number"
                      helpText="Include country code (e.g. +1)"
                      icon="FeatherPhone"
                    >
                      <TextField.Input
                        className="conveythis-no-translate"
                        placeholder="Enter phone number"
                        value={singleRecipientData.phoneNumber}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          handleSingleRecipientChange("phoneNumber", event.target.value)
                        }
                      />
                    </TextField>
                    <div className="flex w-full flex-col items-start gap-4">
                      <Select
                        className="h-auto w-full flex-none"
                        label="Select Template"
                        placeholder="Choose a template"
                        helpText="Select a template to use for your message"
                        icon="FeatherLayoutTemplate"
                        value={selectedTemplate?.id.toString() || ""}
                        onValueChange={(value: string) => handleTemplateSelect(value)}
                      >
                        {templates.map((template) => (
                          <Select.Item key={template.id} value={template.id.toString()}>
                            <span className="conveythis-no-translate">{template.name}</span>
                          </Select.Item>
                        ))}
                      </Select>
                      {renderTemplateVariables()}
                    </div>
                    <Select
                      className="h-auto w-full flex-none"
                      label="Assign Labels (Optional)"
                      placeholder="Choose Label(s)"
                      helpText="Assign optional labels for recipient"
                      icon="FeatherTag"
                      value={undefined}
                      onValueChange={(value: string) => handleSingleRecipientLabelSelect(value)}
                    >
                      {getAvailableLabelsForSingle().map((label) => (
                        <Select.Item key={label.id} value={label.id.toString()}>
                          <span className="conveythis-no-translate">{label.label}</span>
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <div className="flex flex-col items-start gap-6">
                    <div className="flex flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Template Preview
                      </span>
                      {renderTemplatePreview()}
                    </div>
                    {singleRecipientData.assignedLabels.length > 0 && (
                      <div className="flex flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Assigned Labels
                        </span>
                        <div className="conveythis-no-translate w-60 h-48 border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex flex-wrap gap-2 h-full overflow-y-auto">
                            {singleRecipientData.assignedLabels.map((label) => (
                              <CategoryLabel
                                key={label.id}
                                className="h-6 w-auto flex-none"
                                text={label.label}
                                onClick={() => handleSingleRecipientLabelRemove(label.id)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Multiple Recipients Form */}
              {recipientType === "multiple" && (
                <div className="flex w-full items-start gap-6">
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">

                    <div className="flex flex-col items-start gap-2">
                      <Select
                        label="Select Labels"
                        placeholder="Choose Label(s) of recipients"
                        helpText="Select the Label(s) of recipients you want to send the message to"
                        icon="FeatherTag"
                        value={undefined}
                        onValueChange={(value: string) => handleMultipleRecipientsLabelSelect(value)}
                      >
                        {getAvailableLabelsForMultiple().map((label) => (
                          <Select.Item key={label.id} value={label.id.toString()}>
                            <span className="conveythis-no-translate">{label.label}</span>
                          </Select.Item>
                        ))}
                      </Select>
                      {multipleRecipientsData.selectedLabels.length > 0 && (
                        <div className="conveythis-no-translate w-96 h-48 border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex flex-wrap gap-2 h-full overflow-y-auto">
                            {multipleRecipientsData.selectedLabels.map((label) => (
                              <CategoryLabel
                                key={label.id}
                                className="h-6 w-auto flex-none"
                                text={label.label}
                                onClick={() => handleMultipleRecipientsLabelRemove(label.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <Select
                      className="h-auto w-full flex-none"
                      label="Assign New Labels (Optional)"
                      placeholder="Choose New Label(s)"
                      helpText="Assign optional new labels for recipient"
                      icon="FeatherTag"
                      value={undefined}
                      onValueChange={(value: string) => handleMultipleRecipientsNewLabelSelect(value)}
                    >
                      {getAvailableLabelsForNewAssignment().map((label) => (
                        <Select.Item key={label.id} value={label.id.toString()}>
                          <span className="conveythis-no-translate">{label.label}</span>
                        </Select.Item>
                      ))}
                    </Select>
                    <div className="flex w-full flex-col items-start gap-4">
                      <Select
                        className="h-auto w-full flex-none"
                        label="Select Template"
                        placeholder="Choose a template"
                        helpText="Select a template to use for your message"
                        icon="FeatherLayoutTemplate"
                        value={selectedTemplate?.id.toString() || ""}
                        onValueChange={(value: string) => handleTemplateSelect(value)}
                      >
                        {templates.map((template) => (
                          <Select.Item key={template.id} value={template.id.toString()}>
                            <span className="conveythis-no-translate">{template.name}</span>
                          </Select.Item>
                        ))}
                      </Select>
                      {renderTemplateVariables()}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-6">
                    <div className="flex flex-col items-start gap-3">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Template Preview
                      </span>
                      {renderTemplatePreview()}
                    </div>

                    {multipleRecipientsData.newLabels.length > 0 && (
                      <div className="flex flex-col items-start gap-3">
                        <span className="text-body-bold font-body-bold text-default-font">
                          Newly Assigned Labels
                        </span>
                        <div className="conveythis-no-translate w-60 h-48 border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex flex-wrap gap-2 h-full overflow-y-auto">
                            {multipleRecipientsData.newLabels.map((label) => (
                              <CategoryLabel
                                key={label.id}
                                className="h-6 w-auto flex-none"
                                text={label.label}
                                onClick={() => handleMultipleRecipientsNewLabelRemove(label.id)}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex w-full items-center gap-4">
                <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
                  <Button
                    variant="neutral-secondary"
                    icon="FeatherClock"
                    disabled={isSending}
                    onClick={handleScheduleClick}
                  >
                    Schedule
                  </Button>
                  <Button
                    icon="FeatherSend"
                    disabled={isSending || recipientType === "multiple"}
                    onClick={() => handleSendSingleRecipient()}
                  >
                    {isSending ? "Sending..." : "Send Now"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogLayout>

      {/* Broadcast schedule dialog (unchanged) */}
      {isScheduleOpen && recipientType === "multiple" && (
        <DialogLayout open={isScheduleOpen} onOpenChange={setIsScheduleOpen}>
          <div className="flex h-full w-full flex-col items-start gap-6 bg-default-background px-2 py-2">
            <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-4 bg-default-background px-4 py-4">
              <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Schedule Broadcast
                  </span>
                  <span className="text-body font-body text-subtext-color">
                    Choose when to send your broadcast message
                  </span>
                </div>
                <IconButton
                  icon="FeatherX"
                  onClick={() => setIsScheduleOpen(false)}
                />
              </div>
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
                {/* Timezone */}
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Timezone
                  </span>
                  <Select
                    className="h-auto w-full flex-none"
                    label=""
                    placeholder="Select Timezone"
                    helpText=""
                    icon="FeatherGlobe"
                    value={scheduleTimezone}
                    onValueChange={(value) => setScheduleTimezone(value)}
                  >
                    {WHATSAPP_TIMEZONES.map((tz) => (
                      <Select.Item key={tz.value} value={tz.value}>
                        {tz.name}
                      </Select.Item>
                    ))}
                  </Select>
                </div>

                {/* Date + Time row */}
                <div className="flex w-full items-start gap-4">
                  {/* Date picker */}
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      Date
                    </span>
                    <SubframeCore.Popover.Root open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          variant="neutral-secondary"
                          icon="FeatherCalendar"
                          iconRight="FeatherChevronDown"
                        >
                          {scheduleDate.toLocaleDateString()}
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Content
                        side="bottom"
                        align="start"
                        sideOffset={4}
                        asChild={true}
                      >
                        <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
                          <Calendar
                            mode="single"
                            selected={scheduleDate}
                            disabled={{ before: new Date() }}
                            onSelect={(date: Date | undefined) => {
                              if (date) {
                                setScheduleDate(date);
                                setIsDatePopoverOpen(false);
                              }
                            }}
                            initialFocus
                          />
                        </div>
                      </SubframeCore.Popover.Content>
                    </SubframeCore.Popover.Root>
                  </div>

                  {/* Time selectors */}
                  <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      Time
                    </span>
                    <div className="flex w-full items-center gap-2">
                      <Select
                        className="h-auto grow shrink-0 basis-0"
                        label=""
                        placeholder="Hour"
                        helpText=""
                        value={scheduleHour}
                        onValueChange={(value) => setScheduleHour(value)}
                      >
                        {["01","02","03","04","05","06","07","08","09","10","11","12"].map((h) => (
                          <Select.Item key={h} value={h}>{h}</Select.Item>
                        ))}
                      </Select>
                      <Select
                        className="h-auto grow shrink-0 basis-0"
                        label=""
                        placeholder="Min"
                        helpText=""
                        value={scheduleMinute}
                        onValueChange={(value) => setScheduleMinute(value)}
                      >
                        {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map((m) => (
                          <Select.Item key={m} value={m}>{m}</Select.Item>
                        ))}
                      </Select>
                      <ToggleGroup
                        value={scheduleAmPm}
                        onValueChange={(value) => {
                          if (value === "AM" || value === "PM") setScheduleAmPm(value);
                        }}
                      >
                        <ToggleGroup.Item icon={null} value="AM">AM</ToggleGroup.Item>
                        <ToggleGroup.Item icon={null} value="PM">PM</ToggleGroup.Item>
                      </ToggleGroup>
                    </div>
                  </div>
                </div>

                {/* Computed datetime preview — computed directly from state so it updates on every change */}
                {(() => {
                  const parsedHour = parseInt(scheduleHour, 10);
                  const parsedMinute = parseInt(scheduleMinute, 10);
                  let hour24 = parsedHour % 12;
                  if (scheduleAmPm === "PM") hour24 += 12;
                  const scheduled = new Date(scheduleDate);
                  scheduled.setHours(hour24, parsedMinute, 0, 0);
                  const m = moment(scheduled);
                  const now = moment();
                  const diffMs = m.diff(now);
                  const isPast = diffMs <= 0;

                  const totalMinutes = Math.abs(Math.floor(diffMs / 60000));
                  const days = Math.floor(totalMinutes / (60 * 24));
                  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
                  const mins = totalMinutes % 60;

                  const parts: string[] = [];
                  if (days > 0) parts.push(`${days}d`);
                  if (hours > 0) parts.push(`${hours}h`);
                  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
                  const timeLeft = parts.join(" ");

                  return (
                    <div className={`flex w-full items-center gap-2 rounded-md px-3 py-2 ${isPast ? "bg-error-50" : "bg-success-50"}`}>
                      <SubframeCore.Icon
                        name="FeatherClock"
                        className={`text-body ${isPast ? "text-error-600" : "text-success-600"}`}
                      />
                      <div className="flex flex-col gap-0.5">
                        <span className="text-caption-bold font-caption-bold text-default-font">
                          {m.format("MMM D, YYYY [at] hh:mm A")} · {scheduleTimezone}
                        </span>
                        <span className={`text-caption font-caption ${isPast ? "text-error-600" : "text-success-600"}`}>
                          {isPast ? `${timeLeft} ago (past time)` : `in ${timeLeft}`}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="flex w-full items-center justify-end gap-2">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setIsScheduleOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  icon="FeatherClock"
                  disabled={isSending || (() => {
                    const parsedHour = parseInt(scheduleHour, 10);
                    const parsedMinute = parseInt(scheduleMinute, 10);
                    let hour24 = parsedHour % 12;
                    if (scheduleAmPm === "PM") hour24 += 12;
                    const scheduled = new Date(scheduleDate);
                    scheduled.setHours(hour24, parsedMinute, 0, 0);
                    return scheduled.getTime() <= Date.now();
                  })()}
                  onClick={handleScheduleConfirm}
                >
                  {isSending ? "Scheduling..." : "Schedule Broadcast"}
                </Button>
              </div>
            </div>
          </div>
        </DialogLayout>
      )}

      <ScheduleMessage
        open={isSingleSchedulePopupOpen}
        setIsScheduleMessagePopupOpen={setIsSingleSchedulePopupOpen}
        onScheduleSubmit={handleNewChatScheduleFromMenu}
      />
    </>
  );
}

export default ChatSendNewMessage;